import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiConfig from '../../config/apiConfig';
import { forgetPreviousUser } from '../../services/signInState';
import { landingPathAfterSignIn, recordPasswordChangeRequired } from './account/passwordChangeRequired';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Phase 6 — coordinator login OTP state. step='credentials' renders the
  // existing email+password form; step='otp' renders the 6-digit code form.
  const [step, setStep] = useState('credentials');
  const [otpCode, setOtpCode] = useState('');
  const [loginEmail, setLoginEmail] = useState(''); // email to send to /verify
  const [maskedRecipient, setMaskedRecipient] = useState('');
  // Phase 7 — track which login type kicked off the OTP step so /verify and
  // resend hit the right endpoint (coordinator vs portfolio_manager).
  const [loginRole, setLoginRole] = useState('coordinator');
  const navigate = useNavigate();
  const location = useLocation();

  // SSO State
  const [ssoConfig, setSsoConfig] = useState({ providers: [], passwordEnabled: true, hasSSO: false });
  const [ssoLoading, setSsoLoading] = useState(false);
  const [companyIdForSSO, setCompanyIdForSSO] = useState(null);

  // Check for SSO callback (token in URL from backend redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const ssoError = params.get('error');
    const userRole = params.get('role');
    const companyId = params.get('companyId');

    if (ssoError) {
      setError(decodeURIComponent(ssoError));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (token) {
      forgetPreviousUser();
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
      localStorage.setItem('foundations-token', token);
      localStorage.setItem('userRole', userRole || 'coordinator');
      if (companyId) localStorage.setItem('companyId', companyId);
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate(userRole === 'portfolio_manager' ? '/portfolio-dashboard' : '/coordinator-dashboard');
    }
  }, [location, navigate]);

  // Fetch SSO config for a company
  const fetchSSOConfig = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      setSsoLoading(true);
      const response = await fetch(`${apiConfig.getBaseUrl()}/api/auth/sso/config/${companyId}`);
      const data = await response.json();
      setSsoConfig(data);
      setCompanyIdForSSO(companyId);
    } catch (err) {
      setSsoConfig({ providers: [], passwordEnabled: true, hasSSO: false });
    } finally {
      setSsoLoading(false);
    }
  }, []);

  // Handle SSO login
  const handleSSOLogin = (provider) => {
    if (!companyIdForSSO) {
      setError('Please enter your email first to detect your company.');
      return;
    }
    window.location.href = `${apiConfig.getBaseUrl()}/api/auth/sso/login/${provider}?companyId=${companyIdForSSO}`;
  };

  // Check SSO when email field loses focus
  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;
    const domain = email.split('@')[1];
    if (!domain || domain.length < 3) return;
    try {
      setSsoLoading(true);
      const response = await fetch(`${apiConfig.getBaseUrl()}/api/auth/sso/config/domain/${domain}`);
      const data = await response.json();
      if (data.companyId) {
        setCompanyIdForSSO(data.companyId);
        setSsoConfig(data);
      } else {
        setSsoConfig({ providers: [], passwordEnabled: true, hasSSO: false });
        setCompanyIdForSSO(null);
      }
    } catch (err) {
      setSsoConfig({ providers: [], passwordEnabled: true, hasSSO: false });
    } finally {
      setSsoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmedEmail = email.toLowerCase().trim();

    // Try coordinator login first, then portfolio
    try {
      const coordResponse = await fetch(`${apiConfig.getBaseUrl()}/api/auth/coordinator/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password })
      });
      const coordData = await coordResponse.json();

      if (coordData.success) {
        // Phase 6 OTP-gated path — backend sends a 6-digit code and asks the
        // user to verify before issuing a JWT. `coordData.token` is undefined
        // in this case; switch to the OTP form and stash the login email so
        // /coordinator/login/verify can re-bind to the right user.
        if (coordData.step === 'otp_sent') {
          setLoginEmail(coordData.login_email || trimmedEmail);
          setMaskedRecipient(coordData.masked_email || '');
          setLoginRole('coordinator');
          setStep('otp');
          setLoading(false);
          return;
        }
        // Local-dev fallback path (SES not configured) — backend returned a
        // direct JWT, no OTP step.
        forgetPreviousUser();
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', coordData.user.role);
        localStorage.setItem('authToken', coordData.token);
        localStorage.setItem('token', coordData.token);
        localStorage.setItem('foundations-token', coordData.token);
        localStorage.setItem('userId', coordData.user.id);
        localStorage.setItem('userEmail', coordData.user.email);
        localStorage.setItem('companyName', coordData.user.company_name);
        localStorage.setItem('companyId', coordData.user.company_id);
        if (coordData.user.is_superadmin) localStorage.setItem('isSuperadmin', 'true');
        else localStorage.removeItem('isSuperadmin');
        // A superadmin-issued password lands on the forced change screen first.
        recordPasswordChangeRequired(coordData.user.must_change_password === true);
        navigate(landingPathAfterSignIn(coordData.user));
        return;
      }

      // Coordinator failed — try portfolio
      const portfolioResponse = await fetch(`${apiConfig.getBaseUrl()}/api/auth/portfolio/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password })
      });
      const portfolioData = await portfolioResponse.json();

      if (portfolioData.success) {
        // Phase 7 OTP-gated path — same shape as coordinator but role tag
        // distinguishes which /verify endpoint to call later.
        if (portfolioData.step === 'otp_sent') {
          setLoginEmail(portfolioData.login_email || trimmedEmail);
          setMaskedRecipient(portfolioData.masked_email || '');
          setLoginRole('portfolio_manager');
          setStep('otp');
          setLoading(false);
          return;
        }
        // Local-dev fallback path — direct JWT.
        forgetPreviousUser();
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', portfolioData.user.role);
        localStorage.setItem('authToken', portfolioData.token);
        localStorage.setItem('token', portfolioData.token);
        localStorage.setItem('foundations-token', portfolioData.token);
        localStorage.setItem('userId', portfolioData.user.id);
        localStorage.setItem('userEmail', portfolioData.user.email);
        localStorage.setItem('organizationName', portfolioData.user.organization.name);
        localStorage.setItem('organizationType', portfolioData.user.organization.type);
        localStorage.setItem('organizationId', portfolioData.user.organization.id);
        navigate('/portfolio-dashboard');
        return;
      }

      // Both failed
      setError('Invalid credentials. Please check your email and password.');
    } catch (err) {
      setError('Network error. Please check if the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  // Phase 6 + 7 — verify the 6-digit OTP and pick up the JWT the backend
  // withheld during step 1. Branches on loginRole to hit the matching
  // /verify endpoint (coordinator vs portfolio_manager).
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const verifyPath = loginRole === 'portfolio_manager'
        ? '/api/auth/portfolio/login/verify'
        : '/api/auth/coordinator/login/verify';
      const r = await fetch(`${apiConfig.getBaseUrl()}${verifyPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, otp: otpCode }),
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        setError(data.error || 'Verification failed.');
        return;
      }
      forgetPreviousUser();
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', data.user.role);
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('foundations-token', data.token);
      localStorage.setItem('userId', data.user.id);
      localStorage.setItem('userEmail', data.user.email);
      if (loginRole === 'portfolio_manager') {
        localStorage.setItem('organizationName', data.user.organization.name);
        localStorage.setItem('organizationType', data.user.organization.type);
        localStorage.setItem('organizationId', data.user.organization.id);
        navigate('/portfolio-dashboard');
      } else {
        localStorage.setItem('companyName', data.user.company_name);
        localStorage.setItem('companyId', data.user.company_id);
        if (data.user.is_superadmin) localStorage.setItem('isSuperadmin', 'true');
        else localStorage.removeItem('isSuperadmin');
        // A superadmin-issued password lands on the forced change screen first.
        recordPasswordChangeRequired(data.user.must_change_password === true);
        navigate(landingPathAfterSignIn(data.user));
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!loginEmail || !password) {
      setError('Please re-enter your password to resend a code.');
      setStep('credentials');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const loginPath = loginRole === 'portfolio_manager'
        ? '/api/auth/portfolio/login'
        : '/api/auth/coordinator/login';
      const r = await fetch(`${apiConfig.getBaseUrl()}${loginPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password }),
      });
      const data = await r.json();
      if (data.success && data.step === 'otp_sent') {
        setMaskedRecipient(data.masked_email || '');
        setOtpCode('');
      } else {
        setError(data.error || 'Could not resend code.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  // SSO icon components
  const SSOIcons = {
    azure: (
      <svg width="20" height="20" viewBox="0 0 23 23" fill="currentColor">
        <path d="M0 0h10.931v10.931H0V0zm12.069 0H23v10.931H12.069V0zM0 12.069h10.931V23H0V12.069zm12.069 0H23V23H12.069V12.069z"/>
      </svg>
    ),
    okta: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10"/>
      </svg>
    ),
    google: (
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    )
  };

  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at bottom, #172a45 0%, #0a192f 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          padding: '2.5rem'
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ margin: 0, marginBottom: '1rem', lineHeight: 1 }}>
              <img
                src="/risk-aperture-logo.svg"
                alt="Risk Aperture"
                style={{
                  height: 80,
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </h1>
            <p style={{ color: '#a8b2cc', margin: 0, fontSize: '1rem' }}>
              Comprehensive Risk Intelligence
            </p>
            <p style={{ color: '#7a8aaa', margin: '0.25rem 0 0 0', fontSize: '0.75rem', letterSpacing: '0.15em', fontWeight: 600 }}>
              (FOUNDATIONS)
            </p>
          </div>

          {/* Error */}
          {error && (
            <div role="alert" style={{
              marginBottom: '1.5rem',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 68, 68, 0.08)',
              border: '1px solid rgba(255, 68, 68, 0.25)',
              color: '#ff4444',
              borderRadius: '8px',
              fontSize: '0.875rem',
            }}>
              {error}
            </div>
          )}

          {step === 'otp' ? (
            /* Phase 6 — coordinator OTP step */
            <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                <p style={{ color: '#a8b2cc', margin: 0, fontSize: '0.95rem' }}>Check your email</p>
                <p style={{ color: '#7a8aaa', margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
                  We sent a 6-digit code to <strong style={{ color: '#c0c8d8' }}>{maskedRecipient || 'your email'}</strong>
                </p>
              </div>
              <div>
                <label htmlFor="login-otp" style={{ display: 'block', fontSize: '0.875rem', color: '#a8b2cc', marginBottom: '0.5rem' }}>
                  Verification Code
                </label>
                <input
                  id="login-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  placeholder="000000"
                  style={{ ...inputStyle, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                style={{ ...buttonStyle, ...((loading || otpCode.length !== 6) ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
              >
                {loading ? 'Verifying…' : 'Sign In'}
              </button>
              <button type="button" onClick={resendOtp} disabled={loading} style={linkButtonStyle}>
                Resend code
              </button>
              <button
                type="button"
                onClick={() => { setStep('credentials'); setOtpCode(''); setError(''); }}
                style={linkButtonStyle}
              >
                Back to sign in
              </button>
            </form>
          ) : (
            /* Normal login form */
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label htmlFor="email" style={{ display: 'block', fontSize: '0.875rem', color: '#a8b2cc', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  disabled={loading}
                  required
                  autoComplete="email"
                  autoFocus
                  placeholder="you@yourcompany.com"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                />
                {ssoLoading && (
                  <span style={{ fontSize: '0.75rem', color: '#7a8aaa', marginTop: '4px', display: 'block' }}>
                    Checking SSO...
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="password" style={{ display: 'block', fontSize: '0.875rem', color: '#a8b2cc', marginBottom: '0.5rem' }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="current-password"
                    style={{ ...inputStyle, paddingRight: '3rem' }}
                    onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                    style={{
                      position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', color: '#a8b2cc', cursor: 'pointer', fontSize: '1.1rem', padding: 0
                    }}
                  >
                    {showPassword ? '◡' : '◠'}
                  </button>
                </div>
              </div>

              {ssoConfig.passwordEnabled && (
                <button type="submit" disabled={loading} style={{ ...buttonStyle, ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              )}

              {/* SSO buttons */}
              {ssoConfig.hasSSO && ssoConfig.providers.length > 0 && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '1.25rem' }}>
                  <p style={{ textAlign: 'center', color: '#7a8aaa', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    {ssoConfig.passwordEnabled ? 'Or sign in with' : 'Sign in with your company account'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {ssoConfig.providers.includes('azure_ad') && (
                      <button type="button" onClick={() => handleSSOLogin('azure')} style={{ ...ssoButtonStyle, background: '#0078d4', borderColor: '#0078d4' }}>
                        {SSOIcons.azure} Sign in with Microsoft
                      </button>
                    )}
                    {ssoConfig.providers.includes('okta') && (
                      <button type="button" onClick={() => handleSSOLogin('okta')} style={{ ...ssoButtonStyle, background: '#007dc1', borderColor: '#007dc1' }}>
                        {SSOIcons.okta} Sign in with Okta
                      </button>
                    )}
                    {ssoConfig.providers.includes('google') && (
                      <button type="button" onClick={() => handleSSOLogin('google')} style={ssoButtonStyle}>
                        {SSOIcons.google} Sign in with Google
                      </button>
                    )}
                  </div>
                </div>
              )}

            </form>
          )}

          {/* Demo link */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: '#7a8aaa', fontSize: '0.85rem', margin: 0 }}>
              Want to explore first?{' '}
              <a href="/demo" style={{ color: '#00d9ff', textDecoration: 'none' }}>
                Try the self-guided demo
              </a>
            </p>
          </div>
        </div>
      </div>

    </>
  );
};

// Shared styles
const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 255, 255, 0.04)',
  color: '#e6f1ff',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '8px',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

const buttonStyle = {
  width: '100%',
  padding: '0.85rem 1rem',
  background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)',
  color: '#ffffff',
  fontWeight: 600,
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  boxShadow: '0 2px 12px rgba(0, 102, 255, 0.3)',
  fontFamily: 'inherit'
};

const ssoButtonStyle = {
  width: '100%',
  padding: '0.7rem 1rem',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '8px',
  background: 'rgba(255, 255, 255, 0.06)',
  color: '#e6f1ff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: 500,
  gap: '0.5rem',
  fontFamily: 'inherit'
};

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#7a8aaa',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontFamily: 'inherit',
  textDecoration: 'underline',
  padding: 0
};

export default Login;
